import { z } from "zod";

/**
 * Platform-controlled feature flags. Stored as a nested `platformFeatures`
 * map inside the `clubs/{clubId}` document, sibling to `features`.
 *
 * Distinct from `features` (`club-features.ts`): `features` is club-editable
 * via the Settings → Notificaciones tab; `platformFeatures` is controlled
 * by uPadelMatch staff and is NOT visible to clubs. Firestore rules deny
 * client writes to this field — only admins (via the Admin SDK) can flip
 * the flags.
 *
 * Used to gate beta AI features and other platform-controlled rollouts to
 * specific Partner Clubs before general availability. Migrates to Firebase
 * Remote Config when roadmap #152 lands; this field stays as the fallback.
 */
export const PlatformFeaturesSchema = z.object({
  /**
   * Owner-facing AI-generated narrative on the Rendimiento screen. Off by
   * default; flipped on per Partner Club from the Firestore console (or an
   * admin script). Spec: `apps/clubmanager-pwa/docs/ai-narrative-rendimiento.md`.
   */
  aiNarrative: z.boolean().default(false),
  /**
   * Coach / Entrenador role (the 4th system role + class-coach links +
   * per-coach reporting). **No longer a gate** as of the v1.26.0 self-serve GA —
   * the role is live on the owner opt-in `features.coachRoleEnabled` alone
   * (`coachRoleLive`). Retained only as a Beta-badge key
   * (`BETA_PLATFORM_FEATURES`); nothing reads it for gating.
   * Spec: `docs/product/spec/clubmanager/00-roles-and-personas.md`.
   */
  coachRole: z.boolean().default(false),
  /**
   * Torneos (tournaments/leagues — Americano social engine + live leaderboard).
   * **Not a gate** — live on the owner opt-in `features.tournamentsEnabled`
   * alone (`tournamentsLive`). Retained only as a Beta-badge key.
   * Spec: `docs/product/spec/clubmanager/19-tournaments.md`.
   */
  tournaments: z.boolean().default(false),
  // ── Club Módulos entitlement keys (docs/PLAN-clubmanager-pwa.md M1) ───────
  // Every `core/modules/registry.ts` entry carries a `betaKey` here so the
  // catalog is ready for per-module pricing plans without another schema
  // migration: `features.*` stays the owner on/off switch, this layer becomes
  // the platform entitlement ("is this club's plan allowed to turn X on?")
  // the day paid tiers exist. Today every club is entitled to everything
  // (flat per-court pricing, no plan tiers) — default `true`, and none of
  // these four are in `BETA_PLATFORM_FEATURES` (they're mature capabilities,
  // not new launches), so nothing reads them for gating yet.
  /** Online booking page (`/book/{slug}`) module entitlement. Not a gate today. */
  onlineBookings: z.boolean().default(true),
  /** CRM / Clientes module entitlement. Not a gate today. */
  crm: z.boolean().default(true),
  /** POS / Ventas (catalogue + quick-sale) module entitlement. Not a gate today. */
  pos: z.boolean().default(true),
  /** Series (recurring bookings) module entitlement. Not a gate today. */
  series: z.boolean().default(true),
});

export type PlatformFeatures = z.infer<typeof PlatformFeaturesSchema>;

export const DEFAULT_PLATFORM_FEATURES: PlatformFeatures = {
  aiNarrative: false,
  coachRole: false,
  tournaments: false,
  onlineBookings: true,
  crm: true,
  pos: true,
  series: true,
};

/**
 * Platform features currently in public **beta**. A club with the capability
 * enabled sees a small "Beta" badge on the feature's own surface (its Ajustes
 * panel + the live surface) so it knows the feature is new + in testing —
 * turning pilot clubs into willing testers rather than confused users.
 *
 * Single source of truth: the admin catalog and every club-facing badge read
 * `isPlatformFeatureBeta`. Remove a key here the day the feature graduates to
 * general availability and the badge disappears everywhere at once.
 */
export const BETA_PLATFORM_FEATURES: ReadonlySet<keyof PlatformFeatures> =
  // `wallet` removed 2026-07: Monedero is GA + no longer a catalog module (it's
  // a config toggle inside Clientes), so the "Beta" tag in the client ficha was
  // misleading. coachRole/tournaments stay — they drive the module "Nuevo" pill.
  // `onlineBookings` added 2026-08-10: back in the catalog, defaulted OFF, and
  // genuinely new — nobody has ever seen it on the Módulos screen.
  new Set(["aiNarrative", "coachRole", "tournaments", "onlineBookings"]);

export function isPlatformFeatureBeta(key: keyof PlatformFeatures): boolean {
  return BETA_PLATFORM_FEATURES.has(key);
}
